// ============================================================
//  src/pages/bots/BotDetailPage.js
// ============================================================
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation, NavLink, Routes, Route, Navigate } from 'react-router-dom';
import { getBot, toggleBot, deleteBot, importBot, exportBot, updateBot, getBotHealth, getBotProfile, updateBotProfile, uploadBotProfilePhoto, getBotPhoneNumbers } from '../../api/client';
import toast from 'react-hot-toast';
import PhoneRegistrationWizard from './PhoneRegistrationWizard';
import CommandsTab      from './tabs/CommandsTab';
import PanelTab         from './tabs/PanelTab';
import UsersTab         from './tabs/UsersTab';
import LogsTab          from './tabs/LogsTab';
import FlowsTab         from './tabs/FlowsTab';
import ScheduledTab     from './tabs/ScheduledTab';
import AnalyticsTab     from './tabs/AnalyticsTab';
import ActivityTab      from './tabs/ActivityTab';
import PersistentMenuTab from './tabs/PersistentMenuTab';
import EndpointsTab     from './tabs/EndpointsTab';
import TemplatesTab     from './tabs/TemplatesTab';
import StorageTab       from './tabs/StorageTab';
import AutomationsTab   from './tabs/AutomationsTab';
import BroadcastsTab     from './tabs/BroadcastsTab';
import CommerceTab      from './tabs/CommerceTab';
import './BotDetailPage.css';

// ── Token field ───────────────────────────────────────────────
function TokenField({ value }) {
  const [visible, setVisible] = useState(false);
  if (!value) return <span style={{ color:'var(--text-muted)', fontSize:12 }}>Not set</span>;
  return (
    <span style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', minWidth:0, flex:'1 1 auto' }}>
      <span className="mono" style={{ fontSize:12, wordBreak:'break-all', minWidth:0 }}>
        {visible ? value : '•'.repeat(24)}
      </span>
      <button onClick={() => setVisible(v => !v)}
        style={{ background:'none', border:'none', cursor:'pointer', color:'var(--primary)', fontSize:12, fontWeight:700, padding:0, flexShrink:0 }}>
        {visible ? 'Hide' : 'Show'}
      </button>
    </span>
  );
}

function CopyBtn({ value }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(value).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  }
  return (
    <button onClick={copy}
      style={{ background:'none', border:'none', cursor:'pointer', color: copied ? 'var(--success)' : 'var(--primary)', fontSize:12, fontWeight:700, padding:0, flexShrink:0 }}>
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

// ── Import modal ──────────────────────────────────────────────
function ImportModal({ bId, botName, onClose, onImported }) {
  const fileRef             = useRef(null);
  const [file, setFile]     = useState(null);
  const [mode, setMode]     = useState('into');
  const [busy, setBusy]     = useState(false);
  const [result, setResult] = useState(null);

  async function handleImport() {
    if (!file) return toast.error('Select a .zip file first');
    setBusy(true);
    try {
      const res = await importBot(file, mode === 'into' ? bId : '');
      if (res.data?.success) { setResult(res.data); onImported?.(res.data); }
      else toast.error(res.data?.message ?? 'Import failed');
    } catch (e) { toast.error(e?.response?.data?.message ?? 'Network error'); }
    setBusy(false);
  }

  if (result) return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth:480 }}>
        <div className="modal-header">
          <div className="modal-title">✅ Import Successful</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <p style={{ color:'var(--text-muted)', fontSize:13, marginBottom:12 }}>
            {result.imported?.length ?? 0} command(s) imported into <strong>{mode === 'into' ? botName : `#${result.bId}`}</strong>.
          </p>
          {result.imported?.length > 0 && (
            <div className="import-result-list">
              {result.imported.map(t => (
                <div key={t} className="import-result-row"><span className="import-tick">✓</span>{t}</div>
              ))}
            </div>
          )}
        </div>
        <div className="modal-footer"><button className="btn btn-primary" onClick={onClose}>Done</button></div>
      </div>
    </div>
  );

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth:460 }}>
        <div className="modal-header">
          <div className="modal-title">Import Bot</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <p style={{ color:'var(--text-muted)', fontSize:13, marginBottom:16 }}>
            Upload a <strong>.zip</strong> bot package (contains <code>panel.json</code> + <code>*.js</code> command files).
          </p>
          <div className="import-mode-row">
            {[{ v:'into', title:'Import into this bot', desc:<>Adds / updates commands in <strong>{botName}</strong></> },
              { v:'new',  title:'Create new bot',        desc:'Imports as a brand-new bot' }
            ].map(({ v, title, desc }) => (
              <label key={v} className={`import-mode-opt ${mode===v?'selected':''}`} onClick={() => setMode(v)}>
                <span className="import-mode-radio" />
                <div><div className="import-mode-title">{title}</div><div className="import-mode-desc">{desc}</div></div>
              </label>
            ))}
          </div>
          <div
            className={`import-dropzone ${file ? 'has-file' : ''}`}
            onClick={() => fileRef.current?.click()}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) setFile(f); }}
          >
            <input ref={fileRef} type="file" accept=".zip" style={{ display:'none' }} onChange={e => setFile(e.target.files[0] ?? null)} />
            {file
              ? <div className="import-file-name"><span style={{ fontSize:20 }}>📦</span><div><div style={{ fontWeight:700, fontSize:13 }}>{file.name}</div><div style={{ fontSize:12, color:'var(--text-muted)' }}>{(file.size/1024).toFixed(1)} KB — click to change</div></div></div>
              : <div className="import-dropzone-hint"><span style={{ fontSize:28 }}>📂</span><div style={{ fontWeight:700, fontSize:13 }}>Click or drag & drop .zip</div><div style={{ fontSize:12, color:'var(--text-muted)' }}>Max 20 MB</div></div>
            }
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={handleImport} disabled={busy || !file}>
            {busy && <span className="spinner" />}{busy ? 'Importing…' : 'Import'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Edit Bot Modal ────────────────────────────────────────────
function EditBotModal({ bot, onClose, onSaved }) {
  const [form, setForm] = useState({
    bName:          bot.bName          ?? '',
    bToken:         bot.bToken         ?? '',
    bPhoneNumberId: bot.bPhoneNumberId ?? '',
    bWhatsappId:    bot.bWhatsappId    ?? '',
    bWebhookToken:  bot.bWebhookToken  ?? '',
    bAppSecret:     bot.bAppSecret     ?? '',
    bMetaAppId:     bot.bMetaAppId     ?? '',
    // Deliberately NOT pre-filled from bot.bPrivateKey. That field holds
    // the value exactly as stored in the DB -- which is the *encrypted*
    // "encv1:..." blob (see FlowKeyCrypto), not a usable PEM. Populating
    // this textarea with it made the field look like it contained a real
    // key, and saving this form for ANY reason (even just renaming the
    // bot) silently resubmitted that encrypted blob, which the backend
    // then re-encrypted AGAIN -- corrupting the key with no error anywhere.
    // Left blank: "untouched" now genuinely means untouched (see save()).
    bPrivateKey:    '',
    bLanguage:      bot.bLanguage      ?? 'json',
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const hasExistingKey = !!bot.bPrivateKey;

  async function save() {
    if (!form.bName.trim())  return toast.error('Bot name required');
    if (!form.bToken.trim()) return toast.error('WhatsApp token required');
    setBusy(true);
    try {
      // Only send bPrivateKey if the admin actually typed a new one here.
      // Leaving it out entirely (rather than sending '') relies on
      // updateBot()'s partial-update behaviour -- an omitted key means
      // "don't touch this column" -- so an untouched field can never
      // wipe or re-encrypt the existing key.
      const { bPrivateKey, ...rest } = form;
      const payload = bPrivateKey.trim()
        ? { bId: bot.bId, ...rest, bPrivateKey }
        : { bId: bot.bId, ...rest };
      const res = await updateBot(payload);
      if (res.data?.success) {
        toast.success('Bot updated');
        onSaved({ ...bot, ...rest, ...(bPrivateKey.trim() ? { bPrivateKey } : {}) });
        onClose();
      }
      else toast.error(res.data?.message ?? 'Update failed');
    } catch { toast.error('Network error'); }
    setBusy(false);
  }

  const Field = ({ label, k, placeholder, hint }) => (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <input className="form-input" value={form[k]} placeholder={placeholder} onChange={e => set(k, e.target.value)} />
      {hint && <div className="form-hint">{hint}</div>}
    </div>
  );

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Edit Bot Settings</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <Field label="Bot Name" k="bName" placeholder="My WhatsApp Bot" />
          <div style={{ borderTop:'1.5px solid var(--border)', paddingTop:16, marginBottom:4 }}>
            <div className="info-card-title" style={{ marginBottom:14 }}>WhatsApp Credentials</div>
            <Field label="Access Token" k="bToken" placeholder="EAAxxxx…"
              hint="Permanent or temporary token from Meta for Developers." />
            <div className="form-row-2">
              <Field label="Phone Number ID" k="bPhoneNumberId" placeholder="1234567890"
                hint="Found in Meta dashboard → Phone Numbers." />
              <Field label="WABA ID" k="bWhatsappId" placeholder="9876543210"
                hint="WhatsApp Business Account ID — required for Flows." />
            </div>
            <div className="form-row-2">
              <Field label="Webhook Verify Token" k="bWebhookToken" placeholder="my_verify_token" />
              <Field label="App Secret" k="bAppSecret" placeholder="abcdef123456" />
            </div>
            <Field label="Meta App ID" k="bMetaAppId" placeholder="1234567890123456"
              hint="Developer app ID from Meta for Developers → Settings → Basic. Needed only to upload a business profile photo." />
          </div>
          <div className="form-group">
            <label className="form-label">Default command language</label>
            <select className="form-input" value={form.bLanguage} onChange={e => set('bLanguage', e.target.value)}>
              <option value="json">JSON</option>
              <option value="javascript">JavaScript</option>
              <option value="python">Python</option>
            </select>
            <div className="form-hint">
              Pre-selects this language when you create a new Command on this bot. Doesn't affect existing commands or Custom Endpoints.
            </div>
          </div>
          <div style={{ borderTop:'1.5px solid var(--border)', paddingTop:16, marginTop:4 }}>
            <div className="info-card-title" style={{ marginBottom:6 }}>WhatsApp Flows — RSA Private Key</div>
            <div className="form-hint" style={{ marginBottom:10 }}>
              Required to decrypt flow data exchange requests from Meta. Generate with:<br />
              <code>openssl genrsa -out private.pem 2048</code><br />
              Then upload the matching <code>public.pem</code> to Meta → WhatsApp → Flows → Encryption Keys.
            </div>
            <div className="form-group" style={{ marginBottom:0 }}>
              <label className="form-label">
                Private Key (PEM) {hasExistingKey && <span style={{ fontWeight:400, color:'var(--text-secondary)' }}>— a key is already configured</span>}
              </label>
              <textarea
                className="form-input"
                style={{ fontFamily:'monospace', fontSize:11, minHeight:100, resize:'vertical' }}
                value={form.bPrivateKey}
                onChange={e => set('bPrivateKey', e.target.value)}
                placeholder={hasExistingKey
                  ? "Leave blank to keep the existing key. Paste a new PEM here only to replace it."
                  : "-----BEGIN RSA PRIVATE KEY-----\n...\n-----END RSA PRIVATE KEY-----"}
              />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={busy}>
            {busy && <span className="spinner" />}{busy ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── WhatsApp Health card ─────────────────────────────────────
const QUALITY_META = {
  GREEN:   { label: 'High quality',   cls: 'quality-green',   icon: '🟢' },
  YELLOW:  { label: 'Medium quality', cls: 'quality-yellow',  icon: '🟡' },
  RED:     { label: 'Low quality',    cls: 'quality-red',     icon: '🔴' },
  NA:      { label: 'Not enough data yet', cls: 'quality-unknown', icon: '⚪' },
  UNKNOWN: { label: 'Not enough data yet', cls: 'quality-unknown', icon: '⚪' },
};

function formatTier(tier) {
  if (!tier) return null;
  // TIER_10K -> "10K", TIER_UNLIMITED -> "Unlimited"
  const raw = tier.replace(/^TIER_/, '');
  return raw === 'UNLIMITED' ? 'Unlimited' : raw;
}

function timeAgo(iso) {
  if (!iso) return null;
  const secs = Math.max(0, Math.floor((Date.now() - new Date(iso.replace(' ', 'T')).getTime()) / 1000));
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

function WhatsAppHealthCard({ bot, onAddWaba }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError]     = useState('');

  const load = useCallback(async (refresh = false) => {
    refresh ? setRefreshing(true) : setLoading(true);
    setError('');
    try {
      const res = await getBotHealth(bot.bId, refresh);
      if (res.data?.success) {
        setData(res.data);
        if (res.data.qualityChanged || res.data.tierChanged) {
          toast(res.data.qualityChanged
            ? `Quality rating changed: ${res.data.prevQualityRating} → ${res.data.qualityRating}`
            : `Messaging tier changed: ${formatTier(res.data.prevMessagingTier)} → ${formatTier(res.data.messagingTier)}`,
            { icon: '⚠️' });
        }
      } else {
        setError(res.data?.message ?? 'Could not load WhatsApp health.');
      }
    } catch {
      setError('Network error loading WhatsApp health.');
    }
    setLoading(false);
    setRefreshing(false);
  }, [bot.bId]);

  useEffect(() => { load(false); }, [load]);

  if (!bot.bToken || !bot.bPhoneNumberId) return null;

  const quality = data?.qualityRating ? (QUALITY_META[data.qualityRating] ?? QUALITY_META.UNKNOWN) : null;
  const tier    = formatTier(data?.messagingTier);

  return (
    <div className="info-card">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
        <div className="info-card-title" style={{ marginBottom:0 }}>WhatsApp Health</div>
        <button className="btn btn-outline" style={{ padding:'5px 12px', fontSize:12 }}
          onClick={() => load(true)} disabled={loading || refreshing}>
          {refreshing ? <span className="spinner" /> : '↻'} Refresh
        </button>
      </div>

      {loading && !data && <div style={{ fontSize:12, color:'var(--text-muted)' }}>Checking with Meta…</div>}

      {error && <div style={{ fontSize:12, color:'var(--danger, #c0392b)' }}>{error}</div>}

      {data && (
        <>
          <div className="info-row">
            <span className="info-key">Quality Rating</span>
            <span className="info-val">
              {quality
                ? <span className={`quality-badge ${quality.cls}`}>{quality.icon} {data.qualityRating}</span>
                : <span style={{ color:'var(--text-muted)', fontSize:12 }}>—</span>}
              {data.qualityChanged && <span className="health-change-flag">changed</span>}
            </span>
          </div>
          <div className="info-row">
            <span className="info-key">Messaging Tier</span>
            <span className="info-val">
              {tier ? tier : <span style={{ color:'var(--text-muted)', fontSize:12 }}>—</span>}
              {data.tierChanged && <span className="health-change-flag">changed</span>}
            </span>
          </div>
          <div className="info-row">
            <span className="info-key">Verified Name</span>
            <span className="info-val">{data.verifiedName || '—'}</span>
          </div>
          <div className="info-row">
            <span className="info-key">Phone Number</span>
            <span className="info-val">{data.displayPhoneNumber || '—'}</span>
          </div>
          <div className="info-row">
            <span className="info-key">Display Name Status</span>
            <span className="info-val">{data.nameStatus || '—'}</span>
          </div>
          {data.lastError && (
            <div style={{ fontSize:11, color:'var(--danger, #c0392b)', marginTop:8 }}>
              Meta reported an issue: {data.lastError}
            </div>
          )}
          <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:10 }}>
            {data.cached ? 'Cached' : 'Checked'} {timeAgo(data.lastCheckedAt)}
          </div>
        </>
      )}
    </div>
  );
}

// ── Business Profile (about/address/description/email/websites) ──
const VERTICAL_OPTIONS = [
  ['UNDEFINED', 'Not set'], ['OTHER', 'Other'], ['AUTO', 'Automotive'], ['BEAUTY', 'Beauty, Spa & Salon'],
  ['APPAREL', 'Clothing & Apparel'], ['EDU', 'Education'], ['ENTERTAIN', 'Entertainment'],
  ['EVENT_PLAN', 'Event Planning'], ['FINANCE', 'Finance & Banking'], ['GROCERY', 'Grocery'],
  ['GOVT', 'Government'], ['HOTEL', 'Hotel & Lodging'], ['HEALTH', 'Medical & Health'],
  ['NONPROFIT', 'Non-profit'], ['PROF_SERVICES', 'Professional Services'], ['RETAIL', 'Shopping & Retail'],
  ['TRAVEL', 'Travel & Transportation'], ['RESTAURANT', 'Restaurant'], ['NOT_A_BIZ', 'Not a Business'],
];

function BusinessProfileModal({ bot, initial, onClose, onSaved, onPhotoChanged }) {
  const [form, setForm] = useState({
    about:       initial.about       ?? '',
    address:     initial.address     ?? '',
    description: initial.description ?? '',
    email:       initial.email       ?? '',
    vertical:    initial.vertical    ?? 'UNDEFINED',
    website1:    initial.websites?.[0] ?? '',
    website2:    initial.websites?.[1] ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(initial.profilePictureUrl ?? null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const fileInputRef = useRef(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handlePhotoPick(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow picking the same file again later
    if (!file) return;
    if (!['image/jpeg', 'image/png'].includes(file.type)) return toast.error('Photo must be a JPEG or PNG');
    if (file.size > 5 * 1024 * 1024) return toast.error('Photo must be 5MB or smaller');

    setPhotoBusy(true);
    try {
      const res = await uploadBotProfilePhoto(bot.bId, file);
      if (res.data?.success) {
        toast.success('Profile photo updated — Meta may take a minute to show it everywhere');
        const localUrl = URL.createObjectURL(file);
        setPhotoUrl(localUrl); // optimistic local preview
        onPhotoChanged?.(localUrl);
      } else {
        toast.error(res.data?.message ?? 'Photo upload failed');
      }
    } catch (e2) {
      toast.error(e2?.response?.data?.message ?? 'Network error uploading photo');
    }
    setPhotoBusy(false);
  }

  async function save() {
    const websites = [form.website1.trim(), form.website2.trim()].filter(Boolean);
    setBusy(true);
    try {
      const res = await updateBotProfile(bot.bId, {
        about: form.about.trim(),
        address: form.address.trim(),
        description: form.description.trim(),
        email: form.email.trim(),
        vertical: form.vertical,
        websites,
      });
      if (res.data?.success) {
        toast.success('Business profile updated');
        onSaved({ ...form, websites });
        onClose();
      } else {
        toast.error(res.data?.message ?? 'Update failed');
      }
    } catch (e) {
      toast.error(e?.response?.data?.message ?? 'Network error');
    }
    setBusy(false);
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Edit Business Profile</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group" style={{ display:'flex', alignItems:'center', gap:14 }}>
            <div style={{
              width:56, height:56, borderRadius:'50%', overflow:'hidden', flexShrink:0,
              background:'var(--bg-subtle, #f3f4f6)', display:'flex', alignItems:'center', justifyContent:'center',
              border:'1.5px solid var(--border)',
            }}>
              {photoUrl
                ? <img src={photoUrl} alt="Profile" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                : <span style={{ fontSize:20, color:'var(--text-muted)' }}>?</span>}
            </div>
            <div>
              <button type="button" className="btn btn-outline" style={{ padding:'6px 14px', fontSize:12 }}
                onClick={() => fileInputRef.current?.click()} disabled={photoBusy}>
                {photoBusy && <span className="spinner" />}{photoBusy ? 'Uploading…' : 'Change Photo'}
              </button>
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png" style={{ display:'none' }} onChange={handlePhotoPick} />
              <div className="form-hint" style={{ marginTop:4 }}>
                {bot.bMetaAppId
                  ? 'JPEG or PNG, square recommended, max 5MB. Uploads immediately.'
                  : 'Requires a Meta App ID — add one below, save, then reopen this to upload a photo.'}
              </div>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">About</label>
            <input className="form-input" maxLength={139} value={form.about}
              onChange={e => set('about', e.target.value)} placeholder="What your business does, in a few words" />
            <div className="form-hint">{form.about.length}/139 — shown as your status next to the profile photo</div>
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-input" maxLength={256} style={{ minHeight:70, resize:'vertical' }}
              value={form.description} onChange={e => set('description', e.target.value)}
              placeholder="Longer description customers see on your profile" />
            <div className="form-hint">{form.description.length}/256</div>
          </div>
          <div className="form-group">
            <label className="form-label">Address</label>
            <input className="form-input" maxLength={256} value={form.address}
              onChange={e => set('address', e.target.value)} placeholder="Business address" />
          </div>
          <div className="form-row-2">
            <div className="form-group">
              <label className="form-label">Contact Email</label>
              <input className="form-input" maxLength={128} value={form.email}
                onChange={e => set('email', e.target.value)} placeholder="support@yourbusiness.com" />
            </div>
            <div className="form-group">
              <label className="form-label">Industry</label>
              <select className="form-input" value={form.vertical} onChange={e => set('vertical', e.target.value)}>
                {VERTICAL_OPTIONS.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row-2">
            <div className="form-group">
              <label className="form-label">Website 1</label>
              <input className="form-input" maxLength={256} value={form.website1}
                onChange={e => set('website1', e.target.value)} placeholder="https://yourbusiness.com" />
            </div>
            <div className="form-group">
              <label className="form-label">Website 2</label>
              <input className="form-input" maxLength={256} value={form.website2}
                onChange={e => set('website2', e.target.value)} placeholder="https://instagram.com/yourbusiness" />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={busy}>
            {busy && <span className="spinner" />}{busy ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Phone Numbers on this WABA ──────────────────────────────
const PN_QUALITY_META = {
  GREEN:   { icon: '🟢' }, YELLOW: { icon: '🟡' }, RED: { icon: '🔴' },
  NA: { icon: '⚪' }, UNKNOWN: { icon: '⚪' },
};

function timeUntil(dateStr) {
  if (!dateStr) return null;
  const ms = new Date(dateStr).getTime() - Date.now();
  if (ms <= 0) return 'due now';
  const days = Math.floor(ms / 86400000);
  if (days >= 1) return `${days} day${days === 1 ? '' : 's'}`;
  const hours = Math.floor(ms / 3600000);
  return `${Math.max(hours, 1)} hour${hours === 1 ? '' : 's'}`;
}

const RENTAL_STATUS_META = {
  active:          { label: 'Active',          color: '#00875a', bg: '#e7f9f0' },
  paused:          { label: 'Paused',           color: '#6b7280', bg: '#f3f4f6' },
  pending_payment: { label: 'Pending Payment',  color: '#9a7b00', bg: '#fff8e1' },
  deregistered:    { label: 'Deregistered',     color: '#c0392b', bg: '#fdeaea' },
};

function PhoneNumbersCard({ bot }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [showWizard, setShowWizard] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getBotPhoneNumbers(bot.bId);
      if (res.data?.success) setData(res.data);
      else setError(res.data?.message ?? 'Could not load phone numbers.');
    } catch {
      setError('Network error loading phone numbers.');
    }
    setLoading(false);
  }, [bot.bId]);

  useEffect(() => { load(); }, [load]);

  if (!bot.bToken || !bot.bWhatsappId) return null;

  // ── Rented number: restricted, single-number view ──────────────
  // This bot's WABA is the platform's shared Rent Space WABA, not this
  // customer's own -- so this card deliberately never shows the WABA's
  // full number list here. Only their own rented number, ever.
  if (data?.isRental) {
    const r = data.rental;
    const meta = RENTAL_STATUS_META[r.status] ?? RENTAL_STATUS_META.pending_payment;
    const countdown = timeUntil(r.nextBillingAt);
    return (
      <div className="info-card">
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
          <div className="info-card-title" style={{ marginBottom:0 }}>Your Rented Number</div>
          <button className="btn btn-outline" style={{ padding:'5px 12px', fontSize:12 }}
            onClick={load} disabled={loading}>↻ Refresh</button>
        </div>
        <div style={{ marginBottom:12 }}>
          <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:12, background:meta.bg, color:meta.color }}>
            {meta.label}
          </span>
        </div>
        <div className="info-row"><span className="info-key">Number</span><span className="info-val">+{r.displayNumber}</span></div>
        <div className="info-row"><span className="info-key">Display Name</span><span className="info-val">{r.displayName}</span></div>
        <div className="info-row"><span className="info-key">Monthly Fee</span><span className="info-val">₦{Number(r.monthlyAmount).toLocaleString()}</span></div>
        {r.status === 'active' && countdown && (
          <div className="info-row"><span className="info-key">Renews In</span><span className="info-val">{countdown}</span></div>
        )}
        {r.status === 'pending_payment' && (
          <div style={{ fontSize:12, background:'#fff8e1', border:'1px solid #f5e3a8', color:'#9a7b00', borderRadius:8, padding:'8px 10px', marginTop:10 }}>
            Payment hasn't been completed for this number yet — go to Rent Space to finish paying.
          </div>
        )}
        {r.status === 'deregistered' && (
          <div style={{ fontSize:12, background:'#fdeaea', border:'1px solid #f3c6c2', color:'#c0392b', borderRadius:8, padding:'8px 10px', marginTop:10 }}>
            This number has been deregistered and can no longer send or receive messages.
          </div>
        )}
        <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:12 }}>
          This number is rented through this platform, so it isn't managed here the same way as a bot using your
          own WhatsApp Business Account — see Rent Space for billing and renewal.
        </div>
      </div>
    );
  }

  return (
    <div className="info-card">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
        <div className="info-card-title" style={{ marginBottom:0 }}>Phone Numbers on this Account</div>
        <button className="btn btn-outline" style={{ padding:'5px 12px', fontSize:12 }}
          onClick={load} disabled={loading}>↻ Refresh</button>
      </div>

      {loading && !data && <div style={{ fontSize:12, color:'var(--text-muted)' }}>Loading…</div>}
      {error && <div style={{ fontSize:12, color:'var(--danger, #c0392b)' }}>{error}</div>}

      {data && !data.isRental && (
        <>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
            <div style={{ flex:1, height:6, background:'var(--bg-subtle,#f0f2f5)', borderRadius:4, overflow:'hidden' }}>
              <div style={{
                width: `${Math.min(100, (data.count / data.inferredLimit) * 100)}%`,
                height: '100%',
                background: data.atCapacity ? '#c0392b' : '#00875a',
                borderRadius:4,
              }} />
            </div>
            <span style={{ fontSize:12, fontWeight:700, color:'var(--text-dark)', whiteSpace:'nowrap' }}>
              {data.count} of {data.inferredLimit} used
            </span>
          </div>

          <div style={{ fontSize:11, color:'var(--text-muted)', marginBottom:14 }}>
            {data.businessVerified
              ? 'Your business is verified — Meta allows up to 20 numbers per account.'
              : 'Your business isn\'t verified yet — Meta caps unverified accounts at 2 numbers. Get verified in Meta Business Manager to unlock up to 20.'}
            {' '}(This limit is inferred from Meta's published rules, not a number Meta reports directly — actual behavior may vary.)
          </div>

          {data.atCapacity && (
            <div style={{ fontSize:12, background:'#fff8f7', border:'1px solid #f3c6c2', color:'#c0392b', borderRadius:8, padding:'8px 10px', marginBottom:14 }}>
              You're at your inferred limit. {data.businessVerified ? 'Contact Meta support to request a higher limit.' : 'Verify your business in Meta Business Manager to raise this to 20.'}
            </div>
          )}

          {data.numbers.length === 0 ? (
            <div style={{ fontSize:12, color:'var(--text-muted)' }}>No numbers found on this WABA.</div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:6, marginBottom:14 }}>
              {data.numbers.map(n => {
                const q = PN_QUALITY_META[n.qualityRating] ?? PN_QUALITY_META.UNKNOWN;
                return (
                  <div key={n.id} style={{
                    display:'flex', alignItems:'center', gap:8, padding:'8px 10px',
                    background: n.isCurrentBot ? '#f0fdf9' : 'var(--bg-subtle,#fafafa)',
                    border: n.isCurrentBot ? '1px solid #bbf0dd' : '1px solid var(--border)',
                    borderRadius:8, fontSize:12,
                  }}>
                    <span>{q.icon}</span>
                    <span style={{ fontWeight:600, color:'var(--text-dark)' }}>{n.displayPhoneNumber || '—'}</span>
                    <span style={{ color:'var(--text-muted)' }}>{n.verifiedName}</span>
                    {n.isCurrentBot && <span style={{ marginLeft:'auto', fontSize:10, fontWeight:700, color:'#00875a' }}>THIS BOT</span>}
                  </div>
                );
              })}
            </div>
          )}

          <button className="btn btn-outline" style={{ width:'100%', fontSize:12 }}
            onClick={() => setShowWizard(true)} disabled={data.atCapacity}
            title={data.atCapacity ? 'You\'re at your inferred number limit' : ''}>
            + Register a New Number
          </button>
        </>
      )}

      {showWizard && (
        <PhoneRegistrationWizard
          initialToken={bot.bToken}
          initialWabaId={bot.bWhatsappId}
          onClose={() => setShowWizard(false)}
          onComplete={() => { setShowWizard(false); load(); toast.success('Number registered — create a new bot with it to start using it.'); }}
        />
      )}
    </div>
  );
}

function BusinessProfileCard({ bot }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [showEdit, setShowEdit] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getBotProfile(bot.bId);
      if (res.data?.success) setData(res.data);
      else setError(res.data?.message ?? 'Could not load business profile.');
    } catch {
      setError('Network error loading business profile.');
    }
    setLoading(false);
  }, [bot.bId]);

  useEffect(() => { load(); }, [load]);

  if (!bot.bToken || !bot.bPhoneNumberId) return null;

  const verticalLabel = VERTICAL_OPTIONS.find(([val]) => val === data?.vertical)?.[1];
  const isEmpty = data && !data.about && !data.address && !data.description && !data.email && !(data.websites?.length);

  return (
    <div className="info-card">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          {data?.profilePictureUrl && (
            <img src={data.profilePictureUrl} alt="" style={{ width:28, height:28, borderRadius:'50%', objectFit:'cover' }} />
          )}
          <div className="info-card-title" style={{ marginBottom:0 }}>Business Profile</div>
        </div>
        <button className="btn btn-outline" style={{ padding:'5px 12px', fontSize:12 }}
          onClick={() => setShowEdit(true)} disabled={loading}>
          Edit
        </button>
      </div>

      {loading && <div style={{ fontSize:12, color:'var(--text-muted)' }}>Loading…</div>}
      {error && <div style={{ fontSize:12, color:'var(--danger, #c0392b)' }}>{error}</div>}

      {data && isEmpty && (
        <div style={{ fontSize:12, color:'var(--text-muted)' }}>
          No business profile set yet — add an about line, address, and description so customers see a complete profile.
        </div>
      )}

      {data && !isEmpty && (
        <>
          {data.about && <div className="info-row"><span className="info-key">About</span><span className="info-val">{data.about}</span></div>}
          {data.description && <div className="info-row"><span className="info-key">Description</span><span className="info-val">{data.description}</span></div>}
          {data.address && <div className="info-row"><span className="info-key">Address</span><span className="info-val">{data.address}</span></div>}
          {data.email && <div className="info-row"><span className="info-key">Email</span><span className="info-val">{data.email}</span></div>}
          {verticalLabel && verticalLabel !== 'Not set' && <div className="info-row"><span className="info-key">Industry</span><span className="info-val">{verticalLabel}</span></div>}
          {data.websites?.length > 0 && (
            <div className="info-row">
              <span className="info-key">Websites</span>
              <span className="info-val">{data.websites.join(', ')}</span>
            </div>
          )}
        </>
      )}

      {showEdit && data && (
        <BusinessProfileModal
          bot={bot}
          initial={data}
          onClose={() => setShowEdit(false)}
          onSaved={updated => setData(d => ({ ...d, ...updated }))}
          onPhotoChanged={url => setData(d => ({ ...d, profilePictureUrl: url }))}
        />
      )}
    </div>
  );
}

// ── Overview tab ──────────────────────────────────────────────
function OverviewTab({ bot, onToggle, onDelete, onImportDone, onBotUpdated }) {
  const [toggling,   setToggling]   = useState(false);
  const [exporting,  setExporting]  = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showEdit,   setShowEdit]   = useState(false);
  const [showPhoneWizard, setShowPhoneWizard] = useState(false);
  const isActive = bot.bIsActive == 1;
  const navigate = useNavigate();

  async function handleToggle() {
    setToggling(true);
    await onToggle(!isActive);
    setToggling(false);
  }

  async function handleExport() {
    setExporting(true);
    try {
      const res   = await exportBot(bot.bId);
      const cd    = res.headers?.['content-disposition'] ?? '';
      const match = cd.match(/filename="([^"]+)"/);
      const name  = match?.[1] ?? `bot-${bot.bName}-export.zip`;
      const url   = URL.createObjectURL(new Blob([res.data], { type:'application/zip' }));
      const link  = Object.assign(document.createElement('a'), { href:url, download:name });
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch { toast.error('Export failed'); }
    setExporting(false);
  }

  const createdDate = bot.bCreatedAt
    ? new Date(bot.bCreatedAt.replace(' ','T')).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' })
    : '—';

  return (
    <>
      <div className="overview-grid">

        {/* Bot Details */}
        <div className="info-card">
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <div className="info-card-title" style={{ marginBottom:0 }}>Bot Details</div>
            <button className="btn btn-outline" style={{ padding:'5px 12px', fontSize:12 }} onClick={() => setShowEdit(true)}>
              ✏️ Edit
            </button>
          </div>
          <div className="info-row"><span className="info-key">Name</span><span className="info-val">{bot.bName}</span></div>
          <div className="info-row">
            <span className="info-key">Bot ID</span>
            <span className="info-val"><span className="mono">{bot.bId}</span><CopyBtn value={bot.bId} /></span>
          </div>
          <div className="info-row">
            <span className="info-key">Platform</span>
            <span className="info-val"><span className="platform-badge platform-whatsapp">WhatsApp</span></span>
          </div>
          <div className="info-row">
            <span className="info-key">Default command language</span>
            <span className="info-val">{bot.bLanguage ? bot.bLanguage.charAt(0).toUpperCase()+bot.bLanguage.slice(1) : '—'}</span>
          </div>
          <div className="info-row">
            <span className="info-key">Status</span>
            <span className="info-val"><span className={`status-dot ${isActive ? 'active' : 'inactive'}`} />{isActive ? 'Active' : 'Inactive'}</span>
          </div>
          <div className="info-row"><span className="info-key">Created</span><span className="info-val">{createdDate}</span></div>
        </div>

        {/* API Credentials */}
        <div className="info-card">
          <div className="info-card-title">API Credentials</div>

          <div className="info-row" style={{ flexDirection:'column', alignItems:'flex-start', gap:6 }}>
            <span className="info-key">WhatsApp Token</span>
            <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', minWidth:0, width:'100%' }}>
              <TokenField value={bot.bToken} />
              {bot.bToken && <CopyBtn value={bot.bToken} />}
            </div>
          </div>

          <div className="info-row">
            <span className="info-key">Phone Number ID</span>
            <span className="info-val">
              <span className="mono" style={{ fontSize:12 }}>{bot.bPhoneNumberId || '—'}</span>
              {bot.bPhoneNumberId && <CopyBtn value={bot.bPhoneNumberId} />}
            </span>
          </div>
          <div style={{ textAlign:'right', marginTop:-6, marginBottom:6 }}>
            <button className="link-btn" style={{ fontSize:11 }} onClick={() => setShowPhoneWizard(true)}>
              + Register a new number on this WABA
            </button>
          </div>

          <div className="info-row">
            <span className="info-key">WABA ID</span>
            <span className="info-val">
              {bot.bWhatsappId
                ? <><span className="mono" style={{ fontSize:12 }}>{bot.bWhatsappId}</span><CopyBtn value={bot.bWhatsappId} /></>
                : <span style={{ color:'var(--text-muted)', fontSize:12 }}>
                    Not set —{' '}
                    <button className="link-btn" style={{ fontSize:12 }} onClick={() => setShowEdit(true)}>Add now</button>
                  </span>
              }
            </span>
          </div>

          <div className="info-row">
            <span className="info-key">Private Key</span>
            <span className="info-val">
              {bot.bPrivateKey
                ? <span style={{ color:'var(--success)', fontWeight:700, fontSize:12 }}>✓ Configured</span>
                : <span style={{ color:'var(--text-muted)', fontSize:12 }}>
                    Not set —{' '}
                    <button className="link-btn" style={{ fontSize:12 }} onClick={() => navigate(`/bots/${bot.bId}/flows`)}>
                      Generate one now
                    </button>
                  </span>
              }
            </span>
          </div>
          {!bot.bPrivateKey && (
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: -8, marginBottom: 8 }}>
              This isn't something to paste in manually — head to the <strong>Flows</strong> tab and use "Set Up
              Encryption Key" there, which generates a real key pair and uploads the public half to Meta for you
              automatically.
            </div>
          )}

          {/* Flow Exchange URL */}
          <div style={{ marginTop:14, padding:'12px 14px', background:'var(--surface)', borderRadius:'var(--radius-md)', border:'1.5px solid var(--border)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
              <span style={{ fontSize:11, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.7px' }}>
                Flow Endpoint URI
              </span>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', minWidth:0 }}>
              <span className="mono" style={{ fontSize:11, wordBreak:'break-all', color:'var(--text-dark)', flex:'1 1 auto', minWidth:0 }}>
                {`${window.location.origin}/api/bots/exchange/?bId=${bot.bId}`}
              </span>
              <CopyBtn value={`${window.location.origin}/api/bots/exchange/?bId=${bot.bId}`} />
            </div>
            <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:6 }}>
              Use this as the <strong>Endpoint URI</strong> for any WhatsApp Flow on this bot. Handles health checks automatically.
            </div>
          </div>

          {/* Send API Key */}
          <div style={{ marginTop:14, padding:'12px 14px', background:'var(--surface)', borderRadius:'var(--radius-md)', border:'1.5px solid var(--border)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
              <span style={{ fontSize:11, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.7px' }}>
                Send API Key
              </span>
              <span style={{ fontSize:11, background:'#fff3cd', color:'#856404', padding:'1px 8px', borderRadius:10, fontWeight:700 }}>
                Keep secret
              </span>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', minWidth:0 }}>
              <TokenField value={bot.bSendKey} />
              {bot.bSendKey && <CopyBtn value={bot.bSendKey} />}
            </div>
            <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:6 }}>
              Used to send messages via the <code>/api/send/</code> endpoint. Never expose publicly.
            </div>
          </div>
        </div>

        <WhatsAppHealthCard bot={bot} onAddWaba={() => setShowEdit(true)} />

        <PhoneNumbersCard bot={bot} />

        <BusinessProfileCard bot={bot} />

        {/* Stats */}
        <div className="info-card">
          <div className="info-card-title">Stats</div>
          <div className="mini-stats">
            <div className="mini-stat"><div className="mini-stat-val">{bot.totalCommands ?? 0}</div><div className="mini-stat-lbl">Commands</div></div>
            <div className="mini-stat"><div className="mini-stat-val">{bot.totalUsers    ?? 0}</div><div className="mini-stat-lbl">Users</div></div>
          </div>
        </div>

        {/* Import / Export */}
        <div className="info-card">
          <div className="info-card-title">Import &amp; Export</div>
          <div className="action-row">
            <div><div className="action-label">Import Bot</div><div className="action-desc">Upload a .zip bot package — adds commands &amp; panel fields</div></div>
            <button className="btn btn-outline" onClick={() => setShowImport(true)}>📥 Import</button>
          </div>
          <div className="action-row">
            <div><div className="action-label">Export Bot</div><div className="action-desc">Download all commands &amp; panel as a .zip you can re-import</div></div>
            <button className="btn btn-outline" onClick={handleExport} disabled={exporting}>
              {exporting ? <span className="spinner spinner-dark" /> : '📤'}{exporting ? ' Exporting…' : ' Export'}
            </button>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="info-card danger-zone">
          <div className="info-card-title">Actions</div>
          <div className="action-row">
            <div><div className="action-label">{isActive ? 'Deactivate Bot' : 'Activate Bot'}</div><div className="action-desc">{isActive ? 'Bot will stop responding' : 'Bot will start responding'}</div></div>
            <button className={`btn ${isActive ? 'btn-outline' : 'btn-primary'}`} onClick={handleToggle} disabled={toggling}>
              {toggling ? <span className="spinner spinner-dark" /> : (isActive ? 'Deactivate' : 'Activate')}
            </button>
          </div>
          <div className="action-row">
            <div><div className="action-label" style={{ color:'var(--danger)' }}>Delete Bot</div><div className="action-desc">Permanently deletes this bot and all its data</div></div>
            <button className="btn" style={{ background:'var(--danger)', color:'#fff', borderColor:'var(--danger)' }} onClick={onDelete}>Delete</button>
          </div>
        </div>
      </div>

      {showImport && (
        <ImportModal bId={bot.bId} botName={bot.bName} onClose={() => setShowImport(false)}
          onImported={data => { toast.success(`Imported ${data.imported?.length ?? 0} command(s)`); onImportDone?.(); }} />
      )}
      {showEdit && (
        <EditBotModal bot={bot} onClose={() => setShowEdit(false)} onSaved={updated => onBotUpdated?.(updated)} />
      )}
      {showPhoneWizard && (
        <PhoneRegistrationWizard
          initialToken={bot.bToken}
          initialWabaId={bot.bWhatsappId}
          onClose={() => setShowPhoneWizard(false)}
          onComplete={() => {
            setShowPhoneWizard(false);
            toast.success('Number registered — create a new bot with this Phone Number ID to start using it.');
          }}
        />
      )}
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────
export default function BotDetailPage() {
  const { bId }  = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [bot,      setBot]      = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [deleting, setDeleting] = useState(false);

  const loadBot = useCallback(() =>
    getBot(bId)
      .then(res => { if (res.data?.success) setBot(res.data.bot); })
      .catch(() => toast.error('Failed to load bot'))
      .finally(() => setLoading(false))
  , [bId]);

  useEffect(() => { loadBot(); }, [loadBot]);

  async function handleToggle(active) {
    try {
      await toggleBot(bId, active);
      setBot(b => ({ ...b, bIsActive: active ? 1 : 0 }));
      toast.success(active ? 'Bot activated' : 'Bot deactivated');
    } catch { toast.error('Failed to update bot'); }
  }

  async function handleDelete() {
    if (!window.confirm(`Delete "${bot?.bName}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await deleteBot(bId);
      toast.success('Bot deleted');
      navigate('/dashboard');
    } catch { toast.error('Failed to delete bot'); setDeleting(false); }
  }

  // ── Tab definitions ───────────────────────────────────────
  const TABS = [
    { path: '',           label: 'Overview',         fullPath: `/bots/${bId}` },
    { path: 'commands',   label: 'Commands',          fullPath: `/bots/${bId}/commands` },
    { path: 'flows',      label: '⚡ Flows',          fullPath: `/bots/${bId}/flows` },
    { path: 'automations', label: '🤖 Automations',   fullPath: `/bots/${bId}/automations` },
    { path: 'panel',      label: 'Panel',             fullPath: `/bots/${bId}/panel` },
    { path: 'users',      label: 'Users',             fullPath: `/bots/${bId}/users` },
    { path: 'logs',       label: 'Logs',              fullPath: `/bots/${bId}/logs` },
    { path: 'menu',       label: '☰ Menu',            fullPath: `/bots/${bId}/menu` },
    { path: 'scheduled',  label: '🕐 Scheduled',      fullPath: `/bots/${bId}/scheduled` },
    { path: 'analytics',  label: '📊 Analytics',      fullPath: `/bots/${bId}/analytics` },
    { path: 'activity',   label: '👥 Activity',       fullPath: `/bots/${bId}/activity` },
    { path: 'endpoints',  label: '🔌 Endpoints',      fullPath: `/bots/${bId}/endpoints` },
    { path: 'templates',  label: '📝 Templates',      fullPath: `/bots/${bId}/templates` },
    { path: 'storage',    label: '🗄 Storage',        fullPath: `/bots/${bId}/storage` },
    { path: 'broadcasts', label: '📢 Broadcasts',      fullPath: `/bots/${bId}/broadcasts` },
    { path: 'commerce',   label: '🛍️ Commerce',       fullPath: `/bots/${bId}/commerce` },
  ];

  const handleMobileTabChange = (e) => navigate(e.target.value);

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', gap:10, padding:40, color:'var(--text-muted)' }}>
      <span className="spinner spinner-dark" /> Loading bot…
    </div>
  );

  if (!bot) return (
    <div style={{ padding:40 }}>
      <p style={{ color:'var(--text-muted)' }}>Bot not found.</p>
      <button className="btn btn-outline" onClick={() => navigate('/dashboard')}>← Back</button>
    </div>
  );

  const isActive = bot.bIsActive == 1;

  return (
    <div className="bot-detail">
      <div className="breadcrumb">
        <button className="breadcrumb-back" onClick={() => navigate('/dashboard')}>← Dashboard</button>
        <span className="breadcrumb-sep">/</span>
        <span className="breadcrumb-cur">{bot.bName}</span>
      </div>

      <div className="bot-detail-header">
        <div className="bot-detail-avatar">{bot.bName?.[0]?.toUpperCase()}</div>
        <div>
          <h1 className="bot-detail-name">{bot.bName}</h1>
          <div className="bot-detail-meta">
            <span className="platform-badge platform-whatsapp">WhatsApp</span>
            <span className={`status-dot ${isActive ? 'active' : 'inactive'}`} />
            <span style={{ fontSize:13, color:'var(--text-muted)', fontWeight:600 }}>{isActive ? 'Active' : 'Inactive'}</span>
          </div>
        </div>
      </div>

      {/* Mobile Tab Select Dropdown */}
      <div className="mobile-tab-select">
        <select value={location.pathname} onChange={handleMobileTabChange}>
          {TABS.map(tab => (
            <option key={tab.fullPath} value={tab.fullPath}>
              {tab.label}
            </option>
          ))}
        </select>
      </div>

      {/* Desktop Tabs */}
      <div className="detail-tabs-wrap">
        <div className="detail-tabs">
          {TABS.map(({ path, label }) => {
            const to = path ? `/bots/${bId}/${path}` : `/bots/${bId}`;
            return (
              <NavLink key={label} to={to} end={!path}
                className={({ isActive: a }) => `detail-tab${a ? ' active' : ''}`}>
                {label}
              </NavLink>
            );
          })}
        </div>
      </div>

      <div className="detail-content">
        <Routes>
          <Route index element={
            <OverviewTab bot={bot} onToggle={handleToggle} onDelete={handleDelete}
              onImportDone={loadBot} onBotUpdated={updated => setBot(updated)} />
          } />
          <Route path="commands"  element={<CommandsTab bId={bId} bot={bot} />} />
          <Route path="flows"     element={<FlowsTab bId={bId} bot={bot} />} />
          <Route path="automations/*" element={<AutomationsTab bId={bId} />} />
          <Route path="panel"     element={<PanelTab bId={bId} />} />
          <Route path="users"     element={<UsersTab bId={bId} />} />
          <Route path="logs"      element={<LogsTab bId={bId} />} />
          <Route path="menu"      element={<PersistentMenuTab bId={bId} bot={bot} />} />
          <Route path="scheduled" element={<ScheduledTab bId={bId} />} />
          <Route path="analytics" element={<AnalyticsTab bId={bId} />} />
          <Route path="activity"  element={<ActivityTab bId={bId} />} />
          <Route path="endpoints" element={<EndpointsTab bot={bot} />} />
          <Route path="templates" element={<TemplatesTab bot={bot} />} />
          <Route path="storage"   element={<StorageTab bId={bId} />} />
          <Route path="broadcasts" element={<BroadcastsTab bId={bId} />} />
          <Route path="commerce"  element={<CommerceTab bId={bId} />} />
          {/* redirect old icebreakers URLs to menu */}
          <Route path="icebreakers" element={<Navigate to={`/bots/${bId}/menu`} replace />} />
          {/* Inbox moved out to its own top-level sidebar item */}
          <Route path="inbox"       element={<Navigate to={`/inbox/${bId}`} replace />} />
          <Route path="*"         element={<Navigate to={`/bots/${bId}`} replace />} />
        </Routes>
      </div>

      {deleting && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:999 }}>
          <span className="spinner spinner-dark" />
        </div>
      )}
    </div>
  );
}